import { computed } from 'mobx';
import { get } from 'lodash';
import moment from 'moment';

import api from 'core/util/api';
import $dictionary from 'app/stores/$dictionary';
import $notifications from 'app/stores/notifications/$notifications';
import { formatDateTime } from 'core/util/dateUtils';
import { POLICY_APPLICATIONS, ALERT_MANAGER_ACTIVATION_MODES } from '@kentik/ui-shared/alerting/constants';
import { checkApplication } from '@kentik/ui-shared/alerting/applicationUtils';
import { MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP } from '@kentik/ui-shared/mitigations/constants';
import policySerializations from 'app/stores/alerting/policySerializations';
import {
  isBGPNeighborStateChangeAlert,
  isDeviceStateChangePolicy,
  isInterfaceStateChangePolicy
} from 'app/stores/alerting/policyUtils';

import { getMetricOption } from 'app/util/policies';

import BasePolicyModel from './BasePolicyModel';

export const defaultCondition = {
  type: 'static',
  operator: 'greaterThanOrEquals',
  value_select: 'metric_0',
  comparisonValue: 0,
  ratioSettings: {}
};

export const defaultThreshold = {
  severity: 'major',
  enabled: false,
  activate: { times: 5, timeWindow: 2, gracePeriod: 20, timeUnit: 'hour' },
  conditions: [defaultCondition],
  mitigations: [],
  notificationChannels: []
};

class FlowPolicyModel extends BasePolicyModel {
  constructor(attributes, options = {}) {
    const { deserialize } = options;
    super(attributes);

    if (!attributes || Object.keys(attributes).length === 0) {
      attributes = {
        status: 'active',
        evaluationPeriod: '60s',
        baselineSettings: {
          weekendAware: false,
          startOffset: '86400s',
          length: '2419200s',
          minimalLength: '345600s'
        },
        metrics: ['packets'],
        dimensions: ['IP_dst'],
        thresholds: [],
        filters: { connector: 'All', filterGroups: [] }
      };
    }

    // TODO: I had to change this because it broke Policy Updown form.
    // There, we instantiate a policy with some (but not all) of the needed properties.
    // Now if you want to deserialize when instantiating, you have to pass it as an option.

    if (deserialize) {
      this.set(this.deserialize(attributes));
    }
  }

  get omitDuringSerialize() {
    return [
      'rule',
      'uiProperties',
      'explorerPolicy',
      'lastEditTime',
      'creationTime',
      'id',
      'customerID',
      'overall_baseline_setting',
      'metric', // Where is this getting added? There shouldn't be a policy-level metric, only metrics
      'dimension_grouping_enabled',
      'dimensionGroupingOptions.NumGroupingDimensions',
      'is_auto_calc',
      'excludedIPs', // Does this need to be split off and used in a separate request?
      'silenced', // I'm assuming this gets covered by silentModeExpireDate
      'cidr6',
      'cidr'
    ];
  }

  get urlRoot() {
    return '/api/ui/alerting/policies';
  }

  get ruleId() {
    return this.get('activationSettings.ruleId');
  }

  @computed
  get lastModified() {
    return formatDateTime(this.get('lastEditTime'));
  }

  @computed
  get isDdosPolicy() {
    return checkApplication.isDdos(this.application);
  }

  @computed
  get isMetricPolicy() {
    return checkApplication.isMetric(this.application);
  }

  @computed
  get isEventPolicy() {
    return checkApplication.isEvent(this.application);
  }

  @computed
  get isQueryBasedPolicy() {
    return !!this.get('daysToExpire');
  }

  @computed
  get isUpDownPolicy() {
    return this.isMetricPolicy && this.isToggleMode && this.getApplicationMetadata('type') === 'state-change';
  }

  @computed
  get isFromTemplate() {
    return this.getApplicationMetadata('isFromTemplate') === true;
  }

  @computed
  get packageId() {
    return this.isFromTemplate ? this.getApplicationMetadata('packageID') : null;
  }

  @computed
  get isDeviceUpDownPolicy() {
    return this.isUpDownPolicy && isDeviceStateChangePolicy(this);
  }

  @computed
  get isBGPNeighborStateChange() {
    return this.isUpDownPolicy && isBGPNeighborStateChangeAlert(this);
  }

  @computed
  get isInterfaceUpDownPolicy() {
    return this.isUpDownPolicy && isInterfaceStateChangePolicy(this);
  }

  // Activation settings types & checks

  get activationMode() {
    return this.get('activationSettings.mode');
  }

  @computed
  get isToggleMode() {
    return this.activationMode === ALERT_MANAGER_ACTIVATION_MODES.TOGGLE;
  }

  @computed
  get isRollingWindowMode() {
    return this.activationMode === ALERT_MANAGER_ACTIVATION_MODES.ROLLING_WINDOW;
  }

  // Model utility methods

  getApplicationMetadata = (path) => get(this.get('applicationMetadata'), path);

  get sortValues() {
    return {
      primaryDimension: () => this.primaryDimension || '',
      primaryMetric: () => this.primaryMetric || '',
      name: () => this.name.trim().toLowerCase()
    };
  }

  isFlowspecMethodValid = (mitigationMethodData) =>
    !Object.keys(MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP).some((key) =>
      this.hasInvalidDimensionsForFlowspecMatch(mitigationMethodData.flowspec, this.get('dimensions'), key)
    );

  hasInvalidDimensionsForFlowspecMatch = (flowSpecData, dimensions, type) => {
    const data = flowSpecData[`${type}`] || {};
    const { infer, status } = data;

    return infer && status === 'active' && !dimensions.includes(MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP[`${type}`]);
  };

  @computed
  get thresholds() {
    return this.get('thresholds');
  }

  @computed
  get enabled() {
    return this.status === 'active';
  }

  @computed
  get policyError() {
    if (this.status === 'error') {
      return this.get('lastError');
    }

    return false;
  }

  get status() {
    return this.get('status');
  }

  @computed
  get expired() {
    const expireDate = this.get('expireDate');
    return expireDate ? moment(expireDate).isAfter() : false;
  }

  @computed
  get mitigations() {
    const thresholds = this.get('thresholds') || [];
    return thresholds.flatMap((threshold) => threshold.mitigations);
  }

  @computed
  get application() {
    return this.get('application') || POLICY_APPLICATIONS.CORE;
  }

  @computed
  get primaryDimension() {
    const primaryDimension = (this.get('dimensions') || [])[0];
    return $dictionary.dimensionLabelsMap[primaryDimension]?.label || primaryDimension;
  }

  @computed
  get primaryMetric() {
    const primaryMetric = (this.metrics || [])[0];
    return getMetricOption(primaryMetric, this.get('selectedMeasurement')).label;
  }

  get dimensions() {
    return this.get('dimensions') || [];
  }

  get metrics() {
    return this.get('metrics') || [];
  }

  get filters() {
    return this.get('filters') || {};
  }

  removeThresholdsWithNoConditions() {
    const thresholds = this.get('thresholds') || [];
    const newThresholds = thresholds.filter((threshold) => threshold.conditions.length > 0);
    this.set('thresholds', newThresholds);
  }

  addAdditionalNotificationsToPolicy(channelIds = []) {
    this.set(
      'thresholds',
      this.get('thresholds').map((t) => {
        if (!t.enabled) {
          return t;
        }

        return {
          ...t,
          notificationChannels: [...new Set([...t.notificationChannels, ...channelIds])]
        };
      })
    );
  }

  serialize = (data) => {
    const policy = policySerializations.serializeAll(data || this.get());
    return super.serialize(policy);
  };

  deserialize = (data) => {
    let policy = data;
    policy.application = policy.application === '' ? POLICY_APPLICATIONS.CORE : policy.application;

    if (policy.thresholds && policy.thresholds.length) {
      policy.thresholds = $notifications.addNotificationsToThresholds(policy.thresholds, policy);
    }

    policy = policySerializations.deserializeAll(policy);

    return policy;
  };

  enable() {
    return api.post(`${this.urlRoot}/${this.id}/enable`).then(() => {
      this.set('status', 'active');
    });
  }

  disable() {
    return api.post(`${this.urlRoot}/${this.id}/disable`).then(() => {
      this.set('status', 'disabled');
    });
  }

  clonePolicy(overrides = {}) {
    const clonedPolicy = this.duplicate({ save: false, deserialize: false, removeId: true });
    const { name, thresholds, activationSettings } = clonedPolicy.get();

    delete activationSettings.ruleId;

    clonedPolicy.set({
      name: `${name} (Clone)`,
      thresholds: thresholds.map(({ thresholdID, policyID, ...threshold }) => threshold),
      activationSettings,
      minTrafficValue: 0,
      ...overrides
    });

    return clonedPolicy;
  }

  extractSubpolicy(overrides = {}) {
    const subpolicy = this.duplicate({
      save: false,
      deserialize: false,
      removeId: true
    });

    subpolicy.set({
      parentId: `${this.id}`,
      parentID: `${this.id}`,
      isSubpolicy: true,
      status: 'active',
      thresholds: subpolicy.get('thresholds').map(({ id, policy_id, ...threshold }) => threshold),
      activationSettings: { mode: subpolicy.activationMode },
      migrationSettings: {},
      rule: {},
      ...overrides
    });

    return subpolicy;
  }

  async save(...args) {
    let hasNotificationsAttached = false;

    if (args[0]) {
      const saveData = args[0];
      hasNotificationsAttached = saveData.thresholds?.find((t) => t.notificationChannels?.length);
    }

    if (hasNotificationsAttached) {
      return super
        .save(...args)
        .then(() => $notifications.collection.fetch({ force: true }))
        .then(() => {
          this.set('thresholds', $notifications.addNotificationsToThresholds(this.thresholds, this.get()));
        });
    }

    return super.save(...args);
  }
}

export default FlowPolicyModel;
